import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { flatten } from 'typedash';
import { UsersFilter } from '../../../store/admin/users/users-filter.interface';
import { ClientUser, EpsUser, PasswordResetResponse, User } from '../../../store/admin/users/users.interface';
import { Definition } from '../../../store/definitions/definition.interface';
import { util } from '../../../util/util';
import { RestService } from '../rest.service';
import { UserTO } from './user.rest.interface';

@Injectable({ providedIn: 'root' })
export class UserRestService {

  constructor(private restService: RestService) {
  }

  /**
   * Get client user
   * @param id ID of user to get
   * @returns observable containing the client user
   */
  getClientUser(id: string): Observable<ClientUser> {
    const users$: Observable<{
      totalItems: number;
      items: User[];
    }> = this.getUsers({ id, type: 'ClientUser' }, null, null, null, null, this.clientUserMapper.bind(this));
    return users$.pipe(
      first(),
      map(users => (users.totalItems === 1 ? (users.items[0] as ClientUser) : null))
    );
  }

  /**
   * Get eps user
   * @param id ID of user to get
   * @returns observable containing the eps user
   */
  getEpsUser(id: string): Observable<EpsUser> {
    const users$: Observable<{
      totalItems: number;
      items: User[];
    }> = this.getUsers({ id, type: 'EpsUser' }, null, null, null, null, this.epsUserMapper.bind(this));
    return users$.pipe(
      first(),
      map(users => (users.totalItems === 1 ? (users.items[0] as EpsUser) : null))
    );
  }

  /**
   * Get client users
   * @param filter filter to use.  Filter on ClientUser is added automatically
   * @param pageSize how many users per page
   * @param pageNr which page nr to fetch (starts at 1)
   * @param sortField sort on field
   * @param sortAscending sort asc or desc
   * @returns Object containing the users for that page + totalItems & timestamp of snapshot in MW that is used to query on.
   */
  getClientUsers(filter: UsersFilter, pageSize: number, pageNr: number, sortField: string, sortAscending: boolean): Observable<{ totalItems: number; items: User[] }> {
    return this.getUsers({ ...filter, type: 'ClientUser' }, pageSize, pageNr, sortField, sortAscending, this.clientUserMapper.bind(this));
  }

  /**
   * Get eps users
   * @param filter filter to use.  filter on EpsUser is added automatically
   * @param pageSize how many users per page
   * @param pageNr which page nr to fetch (starts at 1)
   * @param sortField sort on field
   * @param sortAscending sort asc or desc
   * @returns Object containing the users for that page + totalItems & timestamp of snapshot in MW that is used to query on.
   */
  getEpsUsers(filter: UsersFilter, pageSize: number, pageNr: number, sortField: string, sortAscending: boolean): Observable<{ totalItems: number; items: User[] }> {
    return this.getUsers({ ...filter, type: 'EpsUser' }, pageSize, pageNr, sortField, sortAscending, this.epsUserMapper.bind(this));
  }

  /**
   * Refresh cached users in MW with users in Active Directory.  Needed to see latest changes in AD in our portal app
   * @returns true (or throws an exception)
   */
  syncWithAD(): Observable<boolean> {
    return this.restService.get<{ Users: any[] }>('/User/triggerAzureActiveDirectorySync').pipe(
      map(response => {
        return true;
      })
    );
  }

  /**
   * Update eps user
   * @returns boolean if it succeeds
   */
  updateEpsUser(user: EpsUser, allSalesOrganisations: Definition[]): Observable<boolean> {
    const salesOrganisations = this.mapSalesOrganisations(user, allSalesOrganisations);
    const body = {
      id: user.id,
      language: util.cleanValue(user.language),
      salesOrganisations,
      firstName: user.firstName,
      lastName: user.lastName,
      isAdmin: !!user.isAdmin,
      isActive: !!user.isActive,
      canRelocate: !!user.canRelocate,
      canSelectDepot: !!user.canSelectDepot,
      canUseHiddenMaterials: !!user.canUseHiddenMaterials,
      adminRoles: {
        updateEpsUser: !!user.updateEpsUser,
        inviteEpsUser: !!user.inviteEpsUser,
        createClientUser: !!user.createClientUser,
        updateClientUser: !!user.updateClientUser,
        inviteClientUser: !!user.inviteClientUser,
        resetPassword: !!user.resetPassword,
        reInvite: !!user.reInvite,
      },
    };
    return this.restService.post<any>('/User/updateUser', body);
  }

  private mapSalesOrganisations(user: EpsUser, allSalesOrganisations: Definition[]) {
    return user.salesOrganisations.reduce((res, salesOrganisationId) => {
      res[salesOrganisationId] = allSalesOrganisations.find(salesOrganisation => salesOrganisation.id === salesOrganisationId).name;
      return res;
    }, {});
  }

  /**
   * invite eps user
   * @returns boolean if it succeeds
   */
  inviteEpsUser(user: EpsUser, salesOrganisations: Definition[]): Observable<boolean> {
    return this.createOrInviteEpsUser(user, 'inviteUser', salesOrganisations);
  }

  /**
   * create eps user
   * @returns boolean if it succeeds
   */
  createEpsUser(user: EpsUser, salesOrganisations: Definition[]): Observable<boolean> {
    return this.createOrInviteEpsUser(user, 'createUser', salesOrganisations);
  }

  /**
   * Update client user
   * @param user the complete user.  id is used as reference to MW, only editable fields are sent in request
   * @returns boolean if it succeeds
   */
  updateClientUser(user: ClientUser): Observable<boolean> {
    const body = {
      id: user.id,
      language: util.cleanValue(user.language),
      firstName: user.firstName,
      lastName: user.lastName,
      alternativeMail: user.alternateEmail,
      customers: user.customers.reduce((res, c) => {
        return {
          ...res,
          [c.soldTo.id]: {
            name: c.soldTo.name,
            roleId: c.role.id,
            shipTos:
              (c.shipTos &&
                c.shipTos.length &&
                c.shipTos.reduce((st, s) => {
                  return { ...st, [s.id]: s.name };
                }, {})) ||
              null,
          },
        };
      }, {}),
      isActive: !!user.isActive,
      useEmailActors: !!user.useEmailActors,
      canAccessHomeMarketBonus: !!user.canAccessHomeMarketBonus,
      isTransporter: !!user.isSuperUser, // TODO 452 rename in future
    };
    return this.restService.post<any>('/User/updateUser', body);
  }

  /**
   * Create new client user
   * @param user the complete user.  id is null, complete user is sent.  MW will do some validation (like uniqueness of email-address)
   * @returns boolean if it succeeds or throws an exception if it fails or request is invalid
   */
  createClientUser(user: ClientUser): Observable<boolean> {
    return this.createOrInviteClientUser(user, 'createUser', resp => resp); // RESPONSE: {"mail":"pierke.pierlala@epswebportal.onmicrosoft.com","password":"EuroPool2018"}
  }

  /**
   * Invite new client user
   * @param user the complete user.  id is null, complete user is sent.  MW will do some validation (like uniqueness of email-address)
   * @returns boolean if it succeeds or throws an exception if it fails or request is invalid
   */
  inviteClientUser(user: ClientUser): Observable<boolean> {
    return this.createOrInviteClientUser(user, 'inviteUser', resp => resp);
  }

  createInviteBulkClientUsersSheetUpload(formData: FormData): Observable<Response> {
    return this.restService.upload('/User/uploadUserSheet', formData);
  }

  downloadExampleBulkClientUsersSheet(): Observable<Blob> {
    return this.restService.getBlobData('/User/downloadUserSheetExample');
  }

  getClientUserDetail(id: string) {
    return this.restService.get<UserTO>(`/User/detailuser?userId=${id}`).pipe(map(response => this.clientUserDetailMapper(response)));
  }

  getEpsUserDetail(id: string) {
    return this.restService.get<UserTO>(`/User/detailuser?userId=${id}`).pipe(map(response => this.epsUserMapper(response)));
  }

  assignToLoop(userId: string, userType: string) {
    return this.restService.post<any>('/User/assignToLoop', {
      userId,
      userType,
    });
  }

  reInviteUser(id: string): Observable<void> {
    return this.restService.post<any>(`/User/reInvite?id=${id}`, null);
  }

  resetPasswordUser(id: string): Observable<PasswordResetResponse> {
    return this.restService.post<any>(`/User/resetPassword?id=${id}`, null).pipe(
      map(response => {
        return response;
      })
    );
  }

  private createOrInviteEpsUser(user: EpsUser, action: String, allSalesOrganisations: Definition[]): Observable<any> {
    const salesOrganisations = this.mapSalesOrganisations(user, allSalesOrganisations);
    const body = {
      language: util.cleanValue(user.language),
      salesOrganisations,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      userType: 'EpsUser',
      isAdmin: !!user.isAdmin,
      isActive: !!user.isActive,
      canRelocate: !!user.canRelocate,
      canSelectDepot: !!user.canSelectDepot,
      canUseHiddenMaterials: !!user.canUseHiddenMaterials,
      adminRoles: {
        updateEpsUser: !!user.updateEpsUser,
        inviteEpsUser: !!user.inviteEpsUser,
        createClientUser: !!user.createClientUser,
        updateClientUser: !!user.updateClientUser,
        inviteClientUser: !!user.inviteClientUser,
        resetPassword: !!user.resetPassword,
        reInvite: !!user.reInvite,
      },
    };
    return this.restService.post<any>(`/User/${action}`, body);
  }

  private createOrInviteClientUser(user: ClientUser, action: String, responseMapper: (any) => any): Observable<boolean> {
    const body = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      language: util.cleanValue(user.language),
      userType: 'ClientUser',
      customers: user.customers.reduce((res, c) => {
        return {
          ...res,
          [c.soldTo.id]: {
            name: c.soldTo.name,
            roleId: c.role.id,
            shipTos:
              (c.shipTos &&
                c.shipTos.length &&
                c.shipTos.reduce((st, s) => {
                  return { ...st, [s.id]: s.name };
                }, {})) ||
              null,
          },
        };
      }, {}),
      isActive: !!user.isActive,
      useEmailActors: !!user.useEmailActors,
      canAccessHomeMarketBonus: !!user.canAccessHomeMarketBonus,
      isTransporter: !!user.isSuperUser, // TODO 452 rename in future
    };

    return this.restService.post<any>(`/User/${action}`, body).pipe(map(responseMapper));
  }

  /**
   * Get users: helper method used by public rest methods
   * @param filter filter.  must contain type: ClientUser or EpsUser
   * @param pageSize how many users per page
   * @param pageNr which page nr to fetch (starts at 1)
   * @param sortField sort on field
   * @param sortAscending sort asc or desc
   * @param mapper that maps the generic user from MW to a specific user (ClientUser or EpsUser) used in front-end
   * @returns Object containing the users for that page + totalItems & timestamp of snapshot in MW that is used to query on.
   */
  private getUsers(
    filter: UsersFilter,
    pageSize: number,
    pageNr: number,
    sortField: string,
    sortAscending: boolean,
    mapper: (any) => any
  ): Observable<{ totalItems: number; items: User[] }> {
    const body = {
      id: filter.id,
      userType: filter.type,
      lastname: filter.lastName,
      firstName: filter.firstName,
      email: filter.email,
      salesOrganisationIds: filter.type === 'EpsUser' ? flatten([filter.salesOrganisationIds]) : null, // make sure it is an array
      soldToIds: filter.type === 'ClientUser' ? filter.soldToIds : null,
      assigned: (filter.isAssigned && filter.isNotAssigned) || (!filter.isAssigned && !filter.isNotAssigned) ? undefined : !!filter.isAssigned,
      IsActive: (filter.isActive && filter.isNotActive) || (!filter.isActive && !filter.isNotActive) ? undefined : !!filter.isActive,
      IsAdmin: (filter.isAdmin && filter.isNotAdmin) || (!filter.isAdmin && !filter.isNotAdmin) ? undefined : !!filter.isAdmin,
      CanSelectDepot: (filter.canSelectDepot && filter.cannotSelectDepot) || (!filter.canSelectDepot && !filter.cannotSelectDepot) ? undefined : !!filter.canSelectDepot,
      CanRelocate: (filter.canRelocate && filter.cannotRelocate) || (!filter.canRelocate && !filter.cannotRelocate) ? undefined : !!filter.canRelocate,
      CanUseHiddenMaterials: (filter.canSeeHiddenMaterials && filter.cannotSeeHiddenMaterials) || (!filter.canSeeHiddenMaterials && !filter.cannotSeeHiddenMaterials) ? undefined : !!filter.canSeeHiddenMaterials,
      UseEmailActors: (filter.usesEmailActors && filter.cannotUseEmailActors) || (!filter.usesEmailActors && !filter.cannotUseEmailActors) ? undefined : !!filter.usesEmailActors,
      IsTransporter: (filter.isTransporter && filter.isNotTransporter) || (!filter.isTransporter && !filter.isNotTransporter) ? undefined : !!filter.isTransporter,
      canAccessHomeMarketBonus: (filter.canAccessHomeMarketBonus && filter.cannotAccessHomeMarketBonus) || (!filter.canAccessHomeMarketBonus && !filter.cannotAccessHomeMarketBonus) ? undefined : !!filter.canAccessHomeMarketBonus,
    };
    const queryParams = {
      pageNumber: pageNr,
      pageSize, // eslint-disable-line
      sortingColumn: sortField,
      sortingType: sortAscending ? 'asc' : 'desc',
    };
    return this.restService
      .post<{
        items: any[];
        totalItems: number;
        lastAzureActiveDirectorySync: number;
      }>(`/User/queryUsers?${util.serializeObjectToParams(queryParams)}`, body)
      .pipe(
        map(response => {
          return {
            totalItems: response.totalItems,
            items: response.items.map(mapper),
            snapshotTimestamp: response.lastAzureActiveDirectorySync,
          };
        })
      );
  }

  downloadUsers(filter: UsersFilter): Observable<object> {
    const newParamsObject = {
      id: filter.id,
      userType: filter.type,
      lastname: filter.lastName,
      firstName: filter.firstName,
      email: filter.email,
      salesOrganisationIds: filter.type === 'EpsUser' ? flatten([filter.salesOrganisationIds]) : null, // make sure it is an array
      soldToIds: filter.type === 'ClientUser' ? filter.soldToIds : null,
      assigned: (filter.isAssigned && filter.isNotAssigned) || (!filter.isAssigned && !filter.isNotAssigned) ? undefined : !!filter.isAssigned,
      IsActive: (filter.isActive && filter.isNotActive) || (!filter.isActive && !filter.isNotActive) ? undefined : !!filter.isActive,
      IsAdmin: (filter.isAdmin && filter.isNotAdmin) || (!filter.isAdmin && !filter.isNotAdmin) ? undefined : !!filter.isAdmin,
      CanSelectDepot: (filter.canSelectDepot && filter.cannotSelectDepot) || (!filter.canSelectDepot && !filter.cannotSelectDepot) ? undefined : !!filter.canSelectDepot,
      CanRelocate: (filter.canRelocate && filter.cannotRelocate) || (!filter.canRelocate && !filter.cannotRelocate) ? undefined : !!filter.canRelocate,
      CanUseHiddenMaterials: (filter.canSeeHiddenMaterials && filter.cannotSeeHiddenMaterials) || (!filter.canSeeHiddenMaterials && !filter.cannotSeeHiddenMaterials) ? undefined : !!filter.canSeeHiddenMaterials,
      UseEmailActors: (filter.usesEmailActors && filter.cannotUseEmailActors) || (!filter.usesEmailActors && !filter.cannotUseEmailActors) ? undefined : !!filter.usesEmailActors,
      IsTransporter: (filter.isTransporter && filter.isNotTransporter) || (!filter.isTransporter && !filter.isNotTransporter) ? undefined : !!filter.isTransporter,
      showForeignUsers: filter.showForeignUsers,
      canAccessHomeMarketBonus: (filter.canAccessHomeMarketBonus && filter.cannotAccessHomeMarketBonus) || (!filter.canAccessHomeMarketBonus && !filter.cannotAccessHomeMarketBonus) ? undefined : !!filter.canAccessHomeMarketBonus,
    };
    return this.restService.getBlobData(`/User/queryUsersAsDocument`, newParamsObject);
  }

  private userMapper(user: UserTO): User {
    return {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      alternateEmail: user.alternativeMail,
      language: user.language,
      status: user.status, // creating or inAD or something like that
      isAssigned: user.isAssigned,
      isActive: user.isActive,
      userType: user.userType,
      invitationAccepted: user.invitationAccepted,
      isAssignedToLoop: user.isAssignedToLoop,
      isKnownInOurDatabase: user.isKnownInOurDatabase,
    };
  }

  private clientUserMapper(user: UserTO): ClientUser {
    return {
      ...this.userMapper(user),
      useEmailActors: user.useEmailActors,
      canAccessHomeMarketBonus: !!user.canAccessHomeMarketBonus,
      isSuperUser: user.isTransporter, // TODO 452 rename in future
      customers: [],
    };
  }

  private clientUserDetailMapper(user: UserTO) {
    return {
      ...this.userMapper(user),
      useEmailActors: user.useEmailActors,
      canAccessHomeMarketBonus: !!user.canAccessHomeMarketBonus,
      isSuperUser: user.isTransporter, // TODO 452 rename in future
      customers: Object.keys(user.customers)
        .map(id => {
          return { id, ...user.customers[id] };
        })
        .map(customer => {
          const shipTos =
            (customer.shipTos &&
              Object.keys(customer.shipTos).map(key => {
                return { id: key, name: customer.shipTos[key] };
              })) ||
            [];
          const shipToNames = shipTos.map(shipTo => shipTo.name).join(' | ') || null;
          return {
            shipTos,
            shipToNames,
            soldTo: { id: customer.id, name: customer.name },
            role: { id: customer.roleId },
          };
        }),
    };
  }

  private epsUserMapper(user: UserTO): EpsUser {
    return {
      ...this.userMapper(user),
      salesOrganisations: user.salesOrganisations && Object.keys(user.salesOrganisations) || [],
      isAdmin: user.isAdmin,
      isAgent: user.isAgent,
      canRelocate: user.canRelocate,
      canSelectDepot: user.canSelectDepot,
      canUseHiddenMaterials: user.canUseHiddenMaterials,
      updateEpsUser: user.isAgent || user.updateEpsUser,
      inviteEpsUser: user.isAgent || user.inviteEpsUser,
      createClientUser: user.isAgent || user.createClientUser,
      updateClientUser: user.isAgent || user.updateClientUser,
      inviteClientUser: user.isAgent || user.inviteClientUser,
      resetPassword: user.isAgent || user.resetPassword,
      reInvite: user.isAgent || user.reInvite,
    };
  }
}
